import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Link,
  withRouter,
} from 'react-router-dom';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl, useIntl } from 'react-intl';

import { APIdeleteDownloadRequest } from '../../api/Downloads';

import { showModal } from '../../actions';
import { showConfirmationModal } from '../../actions/App';

import Avatar from '../Avatar';
import DateTime from '../DateTime';

import {
  userCan,
  userIsAdmin,
} from '../../helpers/Auth';
import { getFileSizeDescription } from '../../helpers/Files';
import { getCurrentURLParams } from '../../helpers/URLParams';
import { getBackButtonMeta } from '../../helpers/App';

const DownloadRequestsListItem = ({
  request,
  profile,
  loadDownloadRequests,
  handleDeleteDownloadRequest,
  handleDownloadFiles,
  handleShareRequest,
  location,
}) => {
  const intl = useIntl();

  const canShare = (request.status === 'accepted' && userCan('share_download_requests')) || userIsAdmin();

  const linkTo = {
    pathname: `/download-requests/${request.uuid}`,
    state: {
      goBackToListButton: {
        ...getBackButtonMeta('download-requests'),
        search: getCurrentURLParams(location),
      },
    },
  };

  return (
    <tr>
      {profile.is_admin && (
        <th scope="row" className="request-user-name user-name">
          <Link to={linkTo}>
            <Avatar user={request.user} />
            <div className="user-name-container">
              {request.user ? request.user.name : '(removed user)'}
              {request.user && (
                <span className="user-activated-by">
                  {request.user.email}
                </span>
              )}
            </div>
          </Link>
        </th>
      )}
      <td className="request-date">
        <DateTime
          id={`request-date-${request.uuid}`}
          dateTime={request.created_at}
          formatted={false}
        />
      </td>
      <td className="request-collection">
        {request.collections.length > 0 ? (
          <>
            {request.collections.map((collection, idx) => (
              <div key={collection.uuid}>
                <Link to={`/collections/${collection.uuid}`}>
                  {collection.name}
                </Link>
                {idx < request.collections.length && (
                  <br />
                )}
              </div>
            ))}
          </>
        ) : (
          <span>-</span>
        )}
      </td>
      <td className="request-files">
        <Link to={linkTo}>
          {request.files_count}
        </Link>
      </td>
      <td className="request-file-sizes">
        {request.file_sizes.map(fs => (
          <div key={fs.id}>
            <span id={`size-${fs.id}-${request.uuid}`} key={fs.id}>
              {fs.name ? fs.name : intl.formatMessage({ defaultMessage: 'Original size' })}
            </span>
            {fs.id !== 'original' && (
              <UncontrolledTooltip fade={false} target={`size-${fs.id}-${request.uuid}`}>
                {`${fs.name}: ${getFileSizeDescription(fs, true)}`}
              </UncontrolledTooltip>
            )}
          </div>
        ))}
      </td>
      <td className="request-status">
        {request.status === 'all' && (
          intl.formatMessage({ defaultMessage: 'all' })
        )}
        {request.status === 'accepted' && (
          intl.formatMessage({ defaultMessage: 'accepted' })
        )}
        {request.status === 'pending' && (
          intl.formatMessage({ defaultMessage: 'pending' })
        )}
        {request.status === 'rejected' && (
          intl.formatMessage({ defaultMessage: 'rejected' })
        )}
      </td>
      <td className="action-more">
        {profile.is_admin ? (
          <UncontrolledDropdown>
            <DropdownToggle nav className="options">
              <i className="far fa-ellipsis-h" />
            </DropdownToggle>
            <DropdownMenu right>
              {request.status === 'accepted' && (
                <DropdownItem
                  onClick={() => {
                    handleDownloadFiles(request);
                  }}
                >
                  <i className="fas fa-download" />
                  {intl.formatMessage({ defaultMessage: 'Download' })}
                </DropdownItem>
              )}
              {canShare && (
                <DropdownItem
                  onClick={() => {
                    handleShareRequest(request);
                  }}
                >
                  <i className="fas fa-share-alt" />
                  {intl.formatMessage({ defaultMessage: 'Share' })}
                </DropdownItem>
              )}
              <Link
                to={linkTo}
              >
                <DropdownItem onClick={() => {}}>
                  <i className="fas fa-pencil" />
                  {intl.formatMessage({ defaultMessage: 'Edit' })}
                </DropdownItem>
              </Link>
              <>
                <DropdownItem divider />
                <DropdownItem
                  onClick={() => {
                    handleDeleteDownloadRequest(request, loadDownloadRequests);
                  }}
                >
                  <i className="fas fa-trash" />
                  {intl.formatMessage({ defaultMessage: 'Delete' })}
                </DropdownItem>
              </>
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : (
          <ul className="download-request-actions-list">
            {request.status === 'accepted' && (
              <li>
                <Link
                  to="#download"
                  id={`download-${request.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadFiles(request);
                  }}
                >
                  <i className="far fa-download" />
                </Link>
                <UncontrolledTooltip fade={false} target={`download-${request.uuid}`}>
                  {intl.formatMessage({ defaultMessage: 'Download' })}
                </UncontrolledTooltip>
              </li>
            )}
            {canShare && (
              <li>
                <Link
                  to="#share"
                  id={`share-${request.uuid}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleShareRequest(request);
                  }}
                >
                  <i className="far fa-share-alt" />
                </Link>
                <UncontrolledTooltip fade={false} target={`share-${request.uuid}`}>
                  {intl.formatMessage({ defaultMessage: 'Share' })}
                </UncontrolledTooltip>
              </li>
            )}
            <li>
              <Link
                to={linkTo}
                id={`view-${request.uuid}`}
              >
                <i className="far fa-file" />
              </Link>
              <UncontrolledTooltip fade={false} target={`view-${request.uuid}`}>
                {intl.formatMessage({ defaultMessage: 'View' })}
              </UncontrolledTooltip>
            </li>
          </ul>
        )}
      </td>
    </tr>
  );
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

const mapDispatchToProps = (dispatch, props) => ({
  handleDeleteDownloadRequest: (request, callback) => {
    const { intl } = props;
    const modalParams = {
      title: intl.formatMessage({ defaultMessage: 'Are you sure?' }),
      text: intl.formatMessage({
        defaultMessage: `Are you sure you want to delete this download request?
          It will be permanently removed and the user will not be notified.`,
      }),
      onConfirm: () => (
        APIdeleteDownloadRequest(request.uuid).then(() => {
          callback();
        })
      ),
    };
    dispatch(showConfirmationModal(modalParams));
  },
  handleDownloadFiles: (request, files = []) => {
    dispatch(showModal('DownloadModal', {
      className: 'modal-download',
      files,
      requestUuid: request.uuid,
      availableFileSizes: request.file_sizes.map(fs => (`${fs.id}`)),
      backdrop: true,
    }));
  },
  handleShareRequest: (request) => {
    dispatch(showModal('ShareModal', {
      className: 'modal-share modal-share-file',
      request,
    }));
  },
});

DownloadRequestsListItem.propTypes = {
  handleDeleteDownloadRequest: PropTypes.func.isRequired,
  handleDownloadFiles: PropTypes.func.isRequired,
  handleShareRequest: PropTypes.func.isRequired,
  request: PropTypes.object.isRequired,
  profile: PropTypes.object.isRequired,
  loadDownloadRequests: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
};

export default withRouter(
  injectIntl(connect(mapStateToProps, mapDispatchToProps)(DownloadRequestsListItem)),
);
